import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const TimeAttendanceModule = () => {
  const [activeSection, setActiveSection] = useState("time-tracking");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleTimeAttendance = () => navigate('/access-recruit');

  const sectionContent = {
    "time-tracking": {
      title: "Flexible Time Tracking",
      description: "Implement comprehensive time tracking across your workforce with multiple clock-in options including biometric devices, mobile apps, web portals, and geofencing technology. Support various work arrangements including office-based, remote, hybrid, and field workers. Track regular hours, overtime, breaks, and meal periods with automated calculations. Integrate with scheduling systems to compare actual hours against planned schedules and identify attendance patterns and trends across the organization."
    },
    "attendance-management": {
      title: "Automated Attendance",
      description: "Streamline attendance monitoring with automated tracking of late arrivals, early departures, absences, and attendance patterns. Implement automated alerts for attendance violations, no-shows, and excessive overtime. Configure attendance policies with grace periods, rounding rules, and compliance requirements. Generate attendance reports, identify attendance trends, and track improvement plans. Support shift-based schedules, flexible hours, and project-based time tracking with customized attendance rules for different employee groups."
    },
    "leave-management": {
      title: "Integrated Leave Management",
      description: "Manage all types of leave requests including vacation, sick leave, personal time, parental leave, and bereavement through automated workflows. Track leave balances, accrual rates, and carry-over policies automatically. Implement approval workflows, delegate management, and automatic balance updates. Provide self-service portals for employees to check balances, submit requests, and view approval status. Generate leave reports, forecast coverage gaps, and ensure compliance with leave policies and regulations."
    },
    "overtime-management": {
      title: "Overtime Tracking",
      description: "Monitor and manage overtime hours with automated tracking, approval workflows, and compliance controls. Configure overtime rules, premium rates, and authorization requirements based on organizational policies and labor regulations. Implement pre-approval workflows for planned overtime and post-approval for emergency situations. Track overtime budgets, analyze overtime patterns, and identify root causes of excessive overtime. Ensure compliance with overtime regulations and fair labor standards across all jurisdictions."
    },
    "attendance-analytics": {
      title: "Attendance Analytics",
      description: "Gain insights into workforce attendance patterns, productivity, and compliance through advanced analytics. Analyze attendance trends, punctuality metrics, absenteeism rates, and overtime patterns. Identify departments or teams with attendance issues and track improvement initiatives. Generate compliance reports for labor regulations and internal policies. Use predictive analytics to forecast attendance patterns and optimize scheduling based on historical data and workforce trends."
    }
  };

  const sections = [
    { id: "time-tracking", label: "TIME TRACKING" },
    { id: "attendance-management", label: "ATTENDANCE" },
    { id: "leave-management", label: "LEAVE" },
    { id: "overtime-management", label: "OVERTIME" },
    { id: "attendance-analytics", label: "ANALYTICS" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Time Tracking */}
        <section
          ref={(el) => (sectionsRef.current["time-tracking"] = el)}
          data-section="time-tracking"
          className={`resto-section ${visibleSections.has("time-tracking") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Time Tracking</div>
            <h2 className="resto-section-title">
              {sectionContent["time-tracking"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["time-tracking"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTimeAttendance}>Clock In/Out</button>
              <button className="resto-secondary-btn" onClick={handleTimeAttendance}>View Time Cards</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/ati1.png" className="resto-real-image" alt="Time Tracking" />
          </div>
        </section>

        {/* 2. Attendance Management */}
        <section
          ref={(el) => (sectionsRef.current["attendance-management"] = el)}
          data-section="attendance-management"
          className={`resto-section resto-reverse ${visibleSections.has("attendance-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Attendance</div>
            <h2 className="resto-section-title">
              {sectionContent["attendance-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["attendance-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTimeAttendance}>Monitor Attendance</button>
              <button className="resto-secondary-btn" onClick={handleTimeAttendance}>Set Policies</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/ati2.png" className="resto-real-image" alt="Attendance Management" />
          </div>
        </section>

        <CTA/>

        {/* 3. Leave Management */}
        <section
          ref={(el) => (sectionsRef.current["leave-management"] = el)}
          data-section="leave-management"
          className={`resto-section ${visibleSections.has("leave-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Leave</div>
            <h2 className="resto-section-title">
              {sectionContent["leave-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["leave-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTimeAttendance}>Request Leave</button>
              <button className="resto-secondary-btn" onClick={handleTimeAttendance}>Manage Approvals</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/ati3.png" className="resto-real-image" alt="Leave Management" />
          </div>
        </section>

        {/* 4. Overtime Management */}
        <section
          ref={(el) => (sectionsRef.current["overtime-management"] = el)}
          data-section="overtime-management"
          className={`resto-section resto-reverse ${visibleSections.has("overtime-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Overtime</div>
            <h2 className="resto-section-title">
              {sectionContent["overtime-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["overtime-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTimeAttendance}>Track Overtime</button>
              <button className="resto-secondary-btn" onClick={handleTimeAttendance}>Configure Rules</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/ati4.png" className="resto-real-image" alt="Overtime Management" />
          </div>
        </section>

        {/* 5. Attendance Analytics */}
        <section
          ref={(el) => (sectionsRef.current["attendance-analytics"] = el)}
          data-section="attendance-analytics"
          className={`resto-section ${visibleSections.has("attendance-analytics") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Analytics</div>
            <h2 className="resto-section-title">
              {sectionContent["attendance-analytics"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["attendance-analytics"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTimeAttendance}>View Analytics</button>
              <button className="resto-secondary-btn" onClick={handleTimeAttendance}>Generate Reports</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/ati5.png" className="resto-real-image" alt="Attendance Analytics" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default TimeAttendanceModule;